import {
  Box,
  Button,
  Typography,
  TextField,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useState, useRef, useEffect } from "react";
import { Upload, Trash2, X, FileText, XCircle } from "lucide-react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { compressImage } from "../utils/imageCompression";
import { useHotel } from "../context/HotelContext";
import { uploadHotelDocument, deleteHotelDocument } from "../services/api";
import LoadingGif from "../components/LoadingGif";

const STEPS = [
  { key: "hotelInfo", labelKey: "StepHotelInfo" },
  { key: "licenseVerification", labelKey: "StepLicenseVerification" },
  { key: "roomInfo", labelKey: "StepRoomInfo" },
  { key: "additionalInfo", labelKey: "StepAdditionalInfo" },
  { key: "paymentMethod", labelKey: "StepPaymentMethod" },
];

const CURRENT_STEP_INDEX = 1;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const HotelAdd_LicenseVerification = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:950px)");
  const { saveStep, fetchDraft, loading, fetchDocuments, hotelData } = useHotel();
  const [businessRegFile, setBusinessRegFile] = useState(null);
  const [animalLicenseFile, setAnimalLicenseFile] = useState(null);
  const [businessRegPreviewUrl, setBusinessRegPreviewUrl] = useState(null);
  const [animalLicensePreviewUrl, setAnimalLicensePreviewUrl] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [businessRegError, setBusinessRegError] = useState(false);
  const [animalLicenseError, setAnimalLicenseError] = useState(false);

  const [isBusinessRegUploading, setIsBusinessRegUploading] = useState(false);
  const [isAnimalLicenseUploading, setIsAnimalLicenseUploading] = useState(false);
  const [businessRegUploaded, setBusinessRegUploaded] = useState(false);
  const [animalLicenseUploaded, setAnimalLicenseUploaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const businessRegInputRef = useRef(null);
  const animalLicenseInputRef = useRef(null);
  const [uploadedDocuments, setUploadedDocuments] = useState(null);

  useEffect(() => {
    if (id) {
      fetchDraft(id);
    }
  }, [id, fetchDraft]);

  useEffect(() => {
    if (id) {
      fetchDocuments().then((response) => {
        const documents = response.data?.data || [];
        const businessReg = documents.find((doc) => doc.type === "businessRegFile");
        const animalLicense = documents.find((doc) => doc.type === "animalLicenseFile");
        setUploadedDocuments({ businessReg, animalLicense });

        if (businessReg) {
          setBusinessRegUploaded(true);
          setBusinessRegFile({ name: businessReg.fileName || "Business Registration Certificate.pdf" });
          const fileUrl = businessReg.fileUrl || businessReg.url || businessReg.link;
          setBusinessRegPreviewUrl(fileUrl);
        }

        if (animalLicense) {
          setAnimalLicenseUploaded(true);
          setAnimalLicenseFile({ name: animalLicense.fileName || "Animal Boarding Licence.pdf" });
          const fileUrl = animalLicense.fileUrl || animalLicense.url || animalLicense.link;
          setAnimalLicensePreviewUrl(fileUrl);
        }
      }).catch((err) => console.error("Failed to fetch documents:", err));
    }
  }, [id, fetchDocuments]);

  const handleFileSelect = async (file, setFile, setError) => {
    if (!file) {
      setError(false);
      setFile(null);
      return;
    }

    const processedFile = file.type.startsWith("image/") ? await compressImage(file) : file;

    if (processedFile.size > MAX_FILE_SIZE_BYTES) {
      setFile(null);
      setError(true);
      return;
    }
    setError(false);
    setFile(processedFile);
  };

  useEffect(() => {
    if (!businessRegFile) {
      setBusinessRegPreviewUrl(null);
      return;
    }
    if (businessRegFile instanceof File) {
      const url = URL.createObjectURL(businessRegFile);
      setBusinessRegPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [businessRegFile]);

  useEffect(() => {
    if (!animalLicenseFile) {
      setAnimalLicensePreviewUrl(null);
      return;
    }
    if (animalLicenseFile instanceof File) {
      const url = URL.createObjectURL(animalLicenseFile);
      setAnimalLicensePreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [animalLicenseFile]);
  const [formData, setFormData] = useState({
    legalRepresentative: "",
    businessBranchName: "",
    registrationCertNumber: "",
    address: "",
    businessRegAuthority: "",
    operationPeriod: "",
    businessNature: "",
  });

  useEffect(() => {
    if (hotelData?.license) {
      setFormData({
        legalRepresentative: hotelData.license.legalRepresentative || "",
        businessBranchName: hotelData.license.businessBranchName || "",
        registrationCertNumber: hotelData.license.registrationCertNumber || "",
        address: hotelData.license.address || "",
        businessRegAuthority: hotelData.license.businessRegAuthority || "",
        operationPeriod: hotelData.license.operationPeriod || "",
        businessNature: hotelData.license.businessNature || "",
      });
    }
  }, [hotelData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileUpload = async (file, setUploading, setUploaded, docType) => {
    if (!file || !id) return;
    try {
      setUploading(true);
      const response = await uploadHotelDocument(id, file, docType);
      if (response.data?.success || response.status === 201) {
        setUploaded(true);
      } else {
        console.error(`Upload failed for ${docType}`);
      }
    } catch (err) {
      console.error(`Failed to upload ${docType}:`, err);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!documentId || !id) return;
    try {
      await deleteHotelDocument(id, documentId);
      setBusinessRegFile(null);
      setBusinessRegPreviewUrl(null);
      setAnimalLicenseFile(null);
      setAnimalLicensePreviewUrl(null);
      setBusinessRegUploaded(false);
      setAnimalLicenseUploaded(false);
      setUploadedDocuments(null);
    } catch (err) {
      console.error("Failed to delete document:", err);
    }
  };

  const handleNext = async () => {
    try {
      setIsSaving(true);
      await saveStep("license", formData);
      navigate(`/hotel/add/license-verification-submit/${id}`);
    } catch (err) {
      console.error("Failed to save step:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndLeave = async () => {
    try {
      await saveStep("license", formData);
      navigate(`/hotel/onboarding`);
    } catch (err) {
      console.error("Failed to save step:", err);
    }
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      borderRadius: "10px",
    },
  };

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  const uploadBoxStyle = {
    display: "flex",
    alignItems: "center",
    gap: 2,
    backgroundColor: "#FFFFFF",
    border: "1.5px solid #D1D1D1",
    borderRadius: "10px",
    padding: isMobile ? "16px" : "20px 24px",
    cursor: "pointer",
    width: "100%",
    boxSizing: "border-box",
  };

  const fileCardStyle = {
    display: "flex",
    alignItems: "center",
    gap: 2,
    backgroundColor: "#FFFFFF",
    border: "1.5px solid #D1D1D1",
    borderRadius: "10px",
    padding: isMobile ? "16px" : "20px 24px",
    width: "100%",
    boxSizing: "border-box",
    cursor: "pointer",
  };

  const isPdf = (file) => file && (file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf"));

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        {/* Stepper */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 6, mx: "auto" }}>
          {STEPS.map((step, index) => (
            <Box key={step.key} sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: isMobile ? "20px" : "30px",
                    height: isMobile ? "20px" : "30px",
                    borderRadius: "50%",
                    backgroundColor: index <= CURRENT_STEP_INDEX ? "#31BACD" : "transparent",
                    border: index <= CURRENT_STEP_INDEX ? "3px solid #fff" : "2px solid #D1D1D1",
                    boxShadow: index <= CURRENT_STEP_INDEX ? "0 0 0 2px #31BACD" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: isMobile ? "10px" : "13px",
                    fontWeight: index === CURRENT_STEP_INDEX ? "600" : "400",
                    color: index === CURRENT_STEP_INDEX ? "#000" : "#666",
                    textAlign: "center",
                    whiteSpace: "wrap",
                    width: isMobile ? "50px" : "100px",
                  }}
                >
                  {t(step.labelKey)}
                </Typography>
              </Box>
              {index < STEPS.length - 1 && (
                <Box
                  sx={{
                    height: "1px",
                    width: isMobile ? "30px" : "200px",
                    backgroundColor: "#D1D1D1",
                    mx: "auto",
                    mb: isMobile ? "18px" : "24px",
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
          {/* Heading */}
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 2, mb: 4, flexWrap: "wrap" }}>
            <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000", display: "flex", alignItems: "center", gap: 1 }}>
              {t('LicenseVerificationTitle')}
              <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "14px", color: "#666" }}>
                {t('MustFill')} <span style={{ color: "red" }}>*</span><span> )</span>
              </Box>
            </Typography>
          </Box>

          {/* Business Registration Certificate */}
          {loading ? (
            <LoadingGif />
          ) : (
            <>
              <Box sx={{ mb: 5 }}>
                <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 0.5 }}>
                  {t('UploadBusinessRegLabel')}<span style={{ color: "red" }}> *</span>
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "12px" : "14px", color: "#666", mb: 2 }}>
                  {t('SupportedFormats')}
                </Typography>

                {!businessRegFile ? (
                  <Box
                    component="label"
                    sx={uploadBoxStyle}
                  >
                    <Box
                      sx={{
                        width: isMobile ? "36px" : "44px",
                        height: isMobile ? "36px" : "44px",
                        borderRadius: "50%",
                        border: "1.5px solid #D1D1D1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Upload size={18} color="#666" />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                        {t('BusinessRegCertLabel')}
                      </Typography>
                      <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#999" }}>
                        {t('BusinessRegCertDesc')}
                      </Typography>
                    </Box>
                    <input
                      ref={businessRegInputRef}
                      type="file"
                      accept=".jpeg,.jpg,.pdf,.png"
                      style={{ display: "none" }}
                      onChange={(e) =>
                        handleFileSelect(e.target.files?.[0] || null, setBusinessRegFile, setBusinessRegError)
                      }
                    />
                  </Box>
                ) : null}
                {businessRegError && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      backgroundColor: "#FFFFFF",
                      border: "1.5px solid #F44336",
                      borderRadius: "10px",
                      padding: isMobile ? "16px" : "20px 24px",
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    <XCircle size={20} color="#F44336" />
                    <Typography sx={{ fontSize: isMobile ? "13px" : "14px", color: "#F44336" }}>
                      {t('FileTooLargeError')}
                    </Typography>
                  </Box>
                )}
                {businessRegFile && (
                  <Box
                    sx={fileCardStyle}
                    onClick={() => {
                      setPreviewFile({ file: businessRegFile, url: businessRegPreviewUrl });
                    }}
                  >
                    <Box
                      sx={{
                        width: isMobile ? "40px" : "48px",
                        height: isMobile ? "40px" : "48px",
                        borderRadius: "6px",
                        overflow: "hidden",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#F5F5F5",
                        border: "1px solid #E0E0E0",
                      }}
                    >
                      {isPdf(businessRegFile) ? (
                        <FileText size={22} color="#999" />
                      ) : (
                        <Box
                          component="img"
                          src={businessRegPreviewUrl}
                          alt={businessRegFile.name}
                          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      )}
                    </Box>
                    <Typography sx={{ flex: 1, fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                      {t('BusinessRegCertLabel')}
                    </Typography>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isBusinessRegUploading) return;
                        if (businessRegUploaded) {
                          handleDeleteDocument(uploadedDocuments?.businessReg?.id);
                        } else {
                          handleFileUpload(businessRegFile, setIsBusinessRegUploading, setBusinessRegUploaded, "businessRegFile");
                        }
                      }}
                      sx={{ minWidth: "auto", padding: "8px", color: businessRegUploaded ? "#666" : "#31BACD", textTransform: "none", }}
                    >
                      {isBusinessRegUploading ? (
                        <CircularProgress size={18} sx={{ color: "#31BACD" }} />
                      ) : businessRegUploaded ? (
                        <Trash2 size={18} />
                      ) : (
                        t('Upload')
                      )}
                    </Button>
                  </Box>
                )}
                {businessRegFile && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 3 }}>
                    <Box>
                      <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
                        {t('LegalRepresentativeLabel')} <span style={{ color: "red" }}> *</span>
                      </Typography>
                      <TextField
                        fullWidth
                        value={formData.legalRepresentative}
                        onChange={(e) => handleInputChange("legalRepresentative", e.target.value)}
                        sx={textFieldSx}
                      />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
                        {t('BusinessBranchNameLabel')} <span style={{ color: "red" }}> *</span>
                      </Typography>
                      <TextField
                        fullWidth
                        value={formData.businessBranchName}
                        onChange={(e) => handleInputChange("businessBranchName", e.target.value)}
                        sx={textFieldSx}
                      />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
                        {t('RegistrationCertNumberLabel')} <span style={{ color: "red" }}> *</span>
                      </Typography>
                      <TextField
                        fullWidth
                        value={formData.registrationCertNumber}
                        onChange={(e) => handleInputChange("registrationCertNumber", e.target.value)}
                        sx={textFieldSx}
                      />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
                        {t('HotelAddressLabel')} <span style={{ color: "red" }}> *</span>
                      </Typography>
                      <TextField
                        fullWidth
                        value={formData.address}
                        onChange={(e) => handleInputChange("address", e.target.value)}
                        sx={textFieldSx}
                      />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
                        {t('BusinessRegAuthorityLabel')} <span style={{ color: "red" }}> *</span>
                      </Typography>
                      <TextField
                        fullWidth
                        value={formData.businessRegAuthority}
                        onChange={(e) => handleInputChange("businessRegAuthority", e.target.value)}
                        sx={textFieldSx}
                      />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
                        {t('OperationPeriodLabel')} <span style={{ color: "red" }}> *</span>
                      </Typography>
                      <TextField
                        fullWidth
                        value={formData.operationPeriod}
                        onChange={(e) => handleInputChange("operationPeriod", e.target.value)}
                        sx={textFieldSx}
                      />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
                        {t('BusinessNatureLabel')} <span style={{ color: "red" }}> *</span>
                      </Typography>
                      <TextField
                        fullWidth
                        value={formData.businessNature}
                        onChange={(e) => handleInputChange("businessNature", e.target.value)}
                        sx={textFieldSx}
                      />
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Animal Boarding Licence */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 0.5 }}>
                  {t('UploadAnimalLicenseLabel')} <span style={{ color: "red" }}> *</span>
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "12px" : "14px", color: "#666", mb: 2 }}>
                  {t('SupportedFormats')}
                </Typography>

                {!animalLicenseFile ? (
                  <Box
                    component="label"
                    sx={uploadBoxStyle}
                  >
                    <Box
                      sx={{
                        width: isMobile ? "36px" : "44px",
                        height: isMobile ? "36px" : "44px",
                        borderRadius: "50%",
                        border: "1.5px solid #D1D1D1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Upload size={18} color="#666" />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                        {t('AnimalLicenseLabel')}
                      </Typography>
                      <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#999" }}>
                        {t('AnimalLicenseDesc')}
                      </Typography>
                    </Box>
                    <input
                      ref={animalLicenseInputRef}
                      type="file"
                      accept=".jpeg,.jpg,.pdf,.png"
                      style={{ display: "none" }}
                      onChange={(e) =>
                        handleFileSelect(e.target.files?.[0] || null, setAnimalLicenseFile, setAnimalLicenseError)
                      }
                    />
                  </Box>
                ) : null}
                {animalLicenseError && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      backgroundColor: "#FFFFFF",
                      border: "1.5px solid #F44336",
                      borderRadius: "10px",
                      padding: isMobile ? "16px" : "20px 24px",
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    <XCircle size={20} color="#F44336" />
                    <Typography sx={{ fontSize: isMobile ? "13px" : "14px", color: "#F44336" }}>
                      {t('FileTooLargeError')}
                    </Typography>
                  </Box>
                )}
                {animalLicenseFile && (
                  <Box
                    sx={fileCardStyle}
                    onClick={() => {
                      setPreviewFile({ file: animalLicenseFile, url: animalLicensePreviewUrl });
                    }}
                  >
                    <Box
                      sx={{
                        width: isMobile ? "40px" : "48px",
                        height: isMobile ? "40px" : "48px",
                        borderRadius: "6px",
                        overflow: "hidden",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#F5F5F5",
                        border: "1px solid #E0E0E0",
                      }}
                    >
                      {isPdf(animalLicenseFile) ? (
                        <FileText size={22} color="#999" />
                      ) : (
                        <Box
                          component="img"
                          src={animalLicensePreviewUrl}
                          alt={animalLicenseFile.name}
                          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      )}
                    </Box>
                    <Typography sx={{ flex: 1, fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                      {t('AnimalLicenseLabel')}{" "}
                      <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "14px", color: "#666" }}>
                        {t('MustFill')}
                      </Box>
                    </Typography>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isAnimalLicenseUploading) return;
                        if (animalLicenseUploaded) {
                          handleDeleteDocument(uploadedDocuments?.animalLicense?.id);
                        } else {
                          handleFileUpload(animalLicenseFile, setIsAnimalLicenseUploading, setAnimalLicenseUploaded, "animalLicenseFile");
                        }
                      }}
                      sx={{ minWidth: "auto", padding: "8px", color: animalLicenseUploaded ? "#666" : "#31BACD", textTransform: "none", }}
                    >
                      {isAnimalLicenseUploading ? (
                        <CircularProgress size={18} sx={{ color: "#31BACD" }} />
                      ) : animalLicenseUploaded ? (
                        <Trash2 size={18} />
                      ) : (
                        t('Upload')
                      )}
                    </Button>
                  </Box>
                )}
              </Box>
            </>
          )}
          {/* Bottom actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Button
              onClick={handleSaveAndLeave}
              disabled={
                !businessRegFile ||
                !animalLicenseFile ||
                businessRegError ||
                animalLicenseError ||
                !businessRegUploaded ||
                !animalLicenseUploaded ||
                !formData.legalRepresentative.trim() ||
                !formData.businessBranchName.trim() ||
                !formData.registrationCertNumber.trim() ||
                !formData.address.trim() ||
                !formData.businessRegAuthority.trim() ||
                !formData.operationPeriod.trim() ||
                !formData.businessNature.trim() ||
                isSaving
              } sx={{
                color: "#666",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {t('SaveAndLeave')}
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate("/hotel/add/Info4/" + id)}
                sx={{
                  color: "#666",
                  borderColor: "#D1D1D1",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { borderColor: "#999", backgroundColor: "transparent" },
                }}
              >
                {t('PreviousStep')}
              </Button>
              <Button
                variant="contained"
                disabled={
                  !businessRegFile ||
                  !animalLicenseFile ||
                  businessRegError ||
                  animalLicenseError ||
                  !businessRegUploaded ||
                  !animalLicenseUploaded ||
                  !formData.legalRepresentative.trim() ||
                  !formData.businessBranchName.trim() ||
                  !formData.registrationCertNumber.trim() ||
                  !formData.address.trim() ||
                  !formData.businessRegAuthority.trim() ||
                  !formData.operationPeriod.trim() ||
                  !formData.businessNature.trim() ||
                  isSaving
                }
                onClick={handleNext}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  "&:disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                {isSaving ? <CircularProgress size={20} color="inherit" /> : t('NextStep')}
              </Button>
            </Box>
          </Box>
        </Box>
      </div>
      <Footer />

      {previewFile && (
        <Box
          onClick={() => setPreviewFile(null)}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "20px" : "40px",
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: "#fff",
              borderRadius: "10px",
              width: "100%",
              maxWidth: "800px",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 16px",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <Typography sx={{ fontWeight: "600", fontSize: "14px", color: "#000" }}>
                {previewFile.file.name}
              </Typography>
              <Button
                onClick={() => setPreviewFile(null)}
                sx={{ minWidth: "auto", padding: "6px", color: "#666" }}
              >
                <X size={20} />
              </Button>
            </Box>
            <Box sx={{ flex: 1, overflow: "auto", backgroundColor: "#F5F5F5" }}>
              {isPdf(previewFile.file) ? (
                <Box
                  component="iframe"
                  src={previewFile.url}
                  onError={() => console.error("Failed to load PDF:", previewFile.url)}
                  sx={{ width: "100%", height: isMobile ? "70vh" : "80vh", border: "none" }}
                />
              ) : (
                <Box
                  component="img"
                  src={previewFile.url}
                  alt={previewFile.file.name}
                  onError={() => console.error("Failed to load image:", previewFile.url)}
                  sx={{ width: "100%", height: "auto", display: "block" }}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default HotelAdd_LicenseVerification;

